import { apiClient } from '@features/shared/api/client'
import type {
  GlobalSearchResponse as GlobalSearchContract,
  MetadataSearchPage,
  RecordSearchPage,
  SearchResponse as SearchContract,
} from './contracts'
import type {
  GlobalSearchResponse,
  SearchMetadataKind,
  SearchMetadataPageResponse,
  SearchRecordPageResponse,
  SearchResponse,
} from '../models'

export interface SearchMatchParams {
  match_type?: 'contains' | 'equals' | 'startsWith' | 'endsWith'
  case_sensitive?: boolean
  whole_word?: boolean
  exact_text?: boolean
}

// Searching a whole table or every table can outrun the client's 30s
// default, so these get longer; a search still running after two minutes is
// stuck, not slow. Typing on cancels the previous search through `signal`,
// so waiting requests don't pile up either.
const SEARCH_AS_YOU_TYPE_TIMEOUT_MS = 120_000

export async function searchRecords(
  tableId: string,
  viewId: string | null,
  query: string,
  limit = 50,
  matchParams: SearchMatchParams = {},
  signal?: AbortSignal
): Promise<SearchResponse> {
  const searchViewId = viewId ?? 'all'
  const { data } = await apiClient.get<SearchContract>(
    `/tables/${tableId}/views/${searchViewId}/search`,
    {
      params: { q: query, limit, ...matchParams },
      timeout: SEARCH_AS_YOU_TYPE_TIMEOUT_MS,
      signal,
    }
  )
  return data
}

export async function searchGlobal(
  query: string,
  limit = 50,
  matchParams: SearchMatchParams = {},
  signal?: AbortSignal
): Promise<GlobalSearchResponse> {
  const { data } = await apiClient.get<GlobalSearchContract>('/search', {
    params: { q: query, limit, ...matchParams },
    timeout: SEARCH_AS_YOU_TYPE_TIMEOUT_MS,
    signal,
  })
  return data
}

export async function searchMetadataPage(
  kind: SearchMetadataKind,
  query: string,
  offset: number,
  limit = 20,
  matchParams: SearchMatchParams = {},
  signal?: AbortSignal
): Promise<SearchMetadataPageResponse> {
  const { data } = await apiClient.get<MetadataSearchPage>('/search/metadata', {
    params: { kind, q: query, offset, limit, ...matchParams },
    // Searching as you type cancels a search typing has moved on from through
    // `signal`, and gives up on one that's stuck; a page asked for once waits.
    timeout: signal ? SEARCH_AS_YOU_TYPE_TIMEOUT_MS : 0,
    signal,
  })
  return { ...data, kind }
}

export async function searchPreviewRecords(
  offset: number,
  limit = 20,
  total?: number
): Promise<SearchRecordPageResponse> {
  const { data } = await apiClient.get<RecordSearchPage>('/search/preview/records', {
    params: { offset, limit, total },
    timeout: 0,
  })
  return data
}

export async function searchPreview(hydrate = false): Promise<GlobalSearchResponse> {
  const { data } = await apiClient.get<GlobalSearchContract>('/search/preview', {
    params: { hydrate },
    timeout: 0,
  })
  return data
}
